import * as types from './actionTypes';

import { AxiosRequestConfig, AxiosResponse } from 'axios';

import API from '../constants/apiEndpoints';
import { appSyncItemNames, constants } from '../constants/constants';
import { msalFetch } from '../components/auth/Auth-Utils';
import { IjobDefault, ThunkResult } from '../models';
import moment from 'moment';
import { AppSyncItemStatus } from '../models-enums';

export function getAllMeasurementPointLists(showError = true): ThunkResult<any> {
    return dispatch => {
        dispatch({
            type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
            name: appSyncItemNames.MeasurementPointLists,
            status: AppSyncItemStatus.inProgress
        });

        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            params: { pagingType: 'none', includeDeleted: true }
        };

        const url = API.GET.measurementPoint.getall;
        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                if (!data.data) {
                    dispatch({
                        type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                        name: appSyncItemNames.MeasurementPointLists,
                        status: AppSyncItemStatus.failed
                    });
                    throw new Error('unable to get inspection questions');
                } else {
                    const measurementPointLists = data.data.result;
                    const listsCount = Array.isArray(measurementPointLists) ? measurementPointLists.length : 0;

                    // Report actual record count for measurement point lists
                    dispatch({
                        type: types.UPDATE_SYNC_ITEM_RECORD_COUNT,
                        name: appSyncItemNames.MeasurementPointLists,
                        recordCount: listsCount
                    });

                    // Dispatch the success action - sync item removal is handled by middleware for async processing
                    dispatch({
                        type: types.MANAGE_MEASUREMENT_POINT_LISTS_SUCCESS,
                        allLists: measurementPointLists,
                        updateDate: moment().unix()
                    });

                    // For small datasets that process synchronously, remove sync item immediately
                    if (!Array.isArray(measurementPointLists) || measurementPointLists.length <= 100) {
                        // Report completion for small datasets
                        dispatch({
                            type: types.UPDATE_SYNC_ITEM_PROGRESS,
                            name: appSyncItemNames.MeasurementPointLists,
                            recordsProcessed: listsCount,
                            recordCount: listsCount
                        });
                        dispatch({
                            type: types.REMOVE_INITIAL_APP_SYNC_ITEM,
                            name: appSyncItemNames.MeasurementPointLists
                        });
                    }
                    // For large datasets, middleware will handle sync item removal after async completion
                    return data;
                }
            })
            .catch((error: any) => {
                console.error('[getAllMeasurementPointLists]:', error);
                dispatch({
                    type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                    name: appSyncItemNames.MeasurementPointLists,
                    status: AppSyncItemStatus.failed
                });
                dispatch({ type: types.MANAGE_MEASUREMENT_POINT_LISTS_FAILED });
                if (showError) {
                    constants.handleError(error, 'get inspection questions');
                }

                throw error;
            });
    };
}

export const saveJobDefaults = (jobDefaults: IjobDefault[]) => ({
    type: types.SAVE_JOB_DEFAULTS,
    payload: jobDefaults
});

export const setSelectedMeasurementPointListByID = (id: string): ThunkResult<any> => {
    return (dispatch, getState) => {
        const { measurementPointListsByID } = getState().measurementPointLists;
        const foundMeasurementPointList = measurementPointListsByID[id];
        if (foundMeasurementPointList) {
            dispatch({
                type: types.SET_SELECTED_MEASUREMENT_POINT_LIST,
                measurementPointList: foundMeasurementPointList
            });
        }
    };
};

/**
 * Process measurement point lists data in a non-blocking way using chunked processing
 */
/* COMMENTED OUT - replaced with async middleware
export function processMeasurementPointListsNonBlocking(
    measurementPointLists: any[],
    updateDate: number
): ThunkResult<any> {
    return (dispatch, getState) => {
        if (!Array.isArray(measurementPointLists) || measurementPointLists.length === 0) {
            // No measurement point lists to process, complete immediately
            dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.MeasurementPointLists });
            return Promise.resolve();
        }

        const chunkSize = getOptimalChunkSize(measurementPointLists.length);

        // Start performance tracking
        perfTracker.startSession(`MP_PROCESSING_${measurementPointLists.length}_items`);
        perfTracker.addEvent('MP_CHUNKING_START', 0, measurementPointLists.length);

        // Process in chunks and dispatch incremental updates to avoid blocking reducers
        let allProcessedLists: any[] = [];

        return processDataInChunks({
            data: measurementPointLists,
            chunkSize,
            processItem: measurementPointList => measurementPointList, // Just pass through without cleaning
            onProgress: (processed, total) => {
                perfTracker.addEvent('MP_CHUNK_PROCESSED', processed, total);
                // Dispatch progress update to keep UI responsive
                dispatch({
                    type: types.MANAGE_MEASUREMENT_POINT_LISTS_PROCESSING_PROGRESS,
                    processed,
                    total
                });
            },
            onComplete: allResults => {
                allProcessedLists = allResults;
            }
        })
            .then(processedMeasurementPointLists => {
                perfTracker.addEvent(
                    'MP_CHUNKING_COMPLETE',
                    processedMeasurementPointLists.length,
                    processedMeasurementPointLists.length
                );

                // Process in smaller chunks to avoid blocking the reducers
                const reducerChunkSize = Math.max(1, Math.floor(chunkSize / 4)); // Smaller chunks for reducer processing
                perfTracker.addEvent('MP_REDUCER_DISPATCH_START', 0, processedMeasurementPointLists.length);

                return new Promise<any[]>(resolve => {
                    let currentIndex = 0;
                    let dispatchCount = 0;

                    function processNextReducerChunk() {
                        const endIndex = Math.min(
                            currentIndex + reducerChunkSize,
                            processedMeasurementPointLists.length
                        );
                        const chunk = processedMeasurementPointLists.slice(currentIndex, endIndex);

                        if (chunk.length > 0) {
                            perfTracker.addEvent(
                                `MP_REDUCER_CHUNK_${dispatchCount}`,
                                currentIndex,
                                processedMeasurementPointLists.length
                            );

                            // Dispatch chunk to reducers
                            dispatch({
                                type: types.MANAGE_MEASUREMENT_POINT_LISTS_SUCCESS,
                                allLists: chunk,
                                updateDate,
                                isIncremental: true // Flag to indicate this is an incremental update
                            });

                            perfTracker.addEvent(
                                `MP_REDUCER_DISPATCH_${dispatchCount}`,
                                currentIndex + chunk.length,
                                processedMeasurementPointLists.length
                            );
                            dispatchCount++;
                        }

                        currentIndex = endIndex;

                        if (currentIndex >= processedMeasurementPointLists.length) {
                            perfTracker.addEvent(
                                'MP_REDUCER_DISPATCH_COMPLETE',
                                processedMeasurementPointLists.length,
                                processedMeasurementPointLists.length
                            );

                            // All chunks processed, remove from sync queue
                            dispatch({
                                type: types.REMOVE_INITIAL_APP_SYNC_ITEM,
                                name: appSyncItemNames.MeasurementPointLists
                            });

                            perfTracker.endSession();
                            resolve(processedMeasurementPointLists);
                        } else {
                            // Yield to UI thread before processing next chunk
                            setTimeout(processNextReducerChunk, 10);
                        }
                    }

                    // Start processing chunks
                    processNextReducerChunk();
                });
            })
            .catch(error => {
                console.error('[processMeasurementPointListsNonBlocking]:', error);

                // Fall back to regular processing on error
                dispatch({
                    type: types.MANAGE_MEASUREMENT_POINT_LISTS_SUCCESS,
                    allLists: measurementPointLists,
                    updateDate
                });

                dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.MeasurementPointLists });
            });
    };
}
*/
